import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, Check, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { UNITS, calcApi } from "@/lib/customCalc";
import { PRESETS, CATEGORIES, addItems, toRate } from "@/lib/itemPresets";

const EMOJIS = ["🍽️", "🍚", "🌾", "🫘", "🥬", "🛢️", "🧂", "🔥", "🥚", "🧆", "🥛", "🍌", "🧁", "🥔", "🍲", "🥜", "🍯", "🧈", "👩‍🍳"];

export function ManualForm({ cfg, set, b, onDone }) {
  const blank = { name: "", name_local: "", emoji: "🍽️", category: "Other", unit: "g", cost_mode: "rate", grain: false, groupKey: "" };
  const [f, setF] = useState(blank);
  const [vals, setVals] = useState({});
  const u = (p) => setF((x) => ({ ...x, ...p }));
  const add = () => {
    if (!f.name.trim()) { toast.error(b("err_item_name")); return; }
    const cells = {};
    Object.entries(vals).forEach(([ck, v]) => {
      if (v.qty === "" && v.val === "") return;
      cells[ck] = f.cost_mode === "price" ? { qty: v.qty || 0, price: v.val || 0, rate: toRate(v.qty, f.unit, v.val) }
        : { qty: v.qty || 0, rate: f.cost_mode === "free" ? 0 : v.val || 0 };
    });
    set((c) => addItems(c, [{ ...f, name: f.name.trim(), groupKey: f.groupKey || undefined, cells }]));
    setF(blank); setVals({}); toast.success(`✓ ${f.name}`);
    if (onDone) onDone();
  };
  return (
    <div className="space-y-2" data-testid="b-manual-form">
      <div className="flex gap-2">
        <select className="input-thali w-16 px-1 text-center" value={f.emoji} onChange={(e) => u({ emoji: e.target.value })} aria-label="icon">{EMOJIS.map((x) => <option key={x}>{x}</option>)}</select>
        <input className="input-thali flex-1" placeholder={b("item_name")} value={f.name} maxLength={40} onChange={(e) => u({ name: e.target.value })} data-testid="b-man-name" />
      </div>
      <input className="input-thali" placeholder={b("local_name")} value={f.name_local} maxLength={40} onChange={(e) => u({ name_local: e.target.value })} data-testid="b-man-local" />
      <div className="grid grid-cols-3 gap-2">
        <select className="input-thali px-2" value={f.category} onChange={(e) => u({ category: e.target.value })} data-testid="b-man-category">{CATEGORIES.map((x) => <option key={x}>{x}</option>)}</select>
        <select className="input-thali px-2" value={f.unit} onChange={(e) => u({ unit: e.target.value })} data-testid="b-man-unit">{UNITS.map((x) => <option key={x}>{x}</option>)}</select>
        <select className="input-thali px-2" value={f.cost_mode} onChange={(e) => u({ cost_mode: e.target.value })} data-testid="b-man-mode">
          <option value="rate">{b("cm_rate")}</option><option value="price">{b("cm_price")}</option><option value="free">{b("cm_free")}</option>
        </select>
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <label className="flex items-center gap-2 text-sm font-semibold min-h-[40px]"><input type="checkbox" className="w-5 h-5 accent-thali-green" checked={f.grain} onChange={(e) => u({ grain: e.target.checked })} /> 🌾 {b("grain")}</label>
        {(cfg.groups || []).length > 0 && (
          <select className="input-thali flex-1 px-2" value={f.groupKey} onChange={(e) => u({ groupKey: e.target.value })} data-testid="b-man-group">
            <option value="">{b("no_group")}</option>{cfg.groups.map((g) => <option key={g.key} value={g.key}>{g.emoji} {g.name}</option>)}
          </select>
        )}
      </div>
      {cfg.classes.map((cl) => (
        <div key={cl.key} className="grid grid-cols-[1fr_80px_80px] gap-2 items-center">
          <span className="text-xs font-semibold truncate">{cl.emoji} {cl.name}</span>
          <input className="input-thali h-11 px-2 text-center" inputMode="decimal" placeholder={b("qty")} value={vals[cl.key]?.qty ?? ""}
            onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && setVals((v) => ({ ...v, [cl.key]: { val: "", ...v[cl.key], qty: e.target.value } }))} data-testid={`b-man-qty-${cl.key}`} />
          <input className="input-thali h-11 px-2 text-center" inputMode="decimal" disabled={f.cost_mode === "free"} placeholder={f.cost_mode === "price" ? "₹/unit" : "₹"} value={vals[cl.key]?.val ?? ""}
            onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && setVals((v) => ({ ...v, [cl.key]: { qty: "", ...v[cl.key], val: e.target.value } }))} data-testid={`b-man-val-${cl.key}`} />
        </div>
      ))}
      <button onClick={add} className="btn-3d-green w-full" data-testid="b-man-add"><Plus className="w-4 h-4" /> {b("add_item")}</button>
    </div>
  );
}

function PresetsTab({ cfg, set, b }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const have = (p) => (p.recipe ? (cfg.groups || []).some((g) => g.name.toLowerCase() === p.name.toLowerCase())
    : cfg.items.some((i) => !i.group && i.name.toLowerCase() === p.name.toLowerCase()));
  const list = PRESETS.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const addOne = (ps) => { set((c) => addItems(c, ps)); setSel([]); toast.success(`✓ ${ps.length}`); };
  return (
    <div className="space-y-3" data-testid="b-presets">
      <div className="flex items-center gap-2 input-thali"><Search className="w-4 h-4 text-thali-ink/40" /><input className="flex-1 bg-transparent outline-none" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="b-preset-search" /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {list.map((p) => {
          const added = have(p); const on = sel.includes(p.name);
          return (
            <div key={p.name} className={`rounded-xl border-2 p-2 flex items-center gap-2 ${on ? "border-thali-saffron bg-thali-saffron/10" : "border-thali-turmeric/30 bg-white"}`} data-testid={`b-preset-${p.name.replace(/\W+/g, "-").toLowerCase()}`}>
              <input type="checkbox" className="w-5 h-5 accent-thali-saffron shrink-0" disabled={added} checked={on} onChange={() => setSel((s) => (on ? s.filter((x) => x !== p.name) : [...s, p.name]))} aria-label={p.name} />
              <button disabled={added} onClick={() => addOne([p])} className="flex-1 text-left min-w-0">
                <div className="text-sm font-semibold truncate">{p.emoji} {p.name}</div>
                <div className="text-[10px] text-thali-ink/50">{added ? <span className="text-thali-green font-bold">✓ {b("added")}</span> : `${p.category} · ${p.unit} · ${b(`cm_${p.cost_mode}`)}${p.recipe ? ` · ${p.recipe.length}` : ""}`}</div>
              </button>
            </div>
          );
        })}
      </div>
      {sel.length > 0 && <button onClick={() => addOne(PRESETS.filter((p) => sel.includes(p.name)))} className="btn-3d-saffron w-full" data-testid="b-preset-add-selected"><Check className="w-4 h-4" /> {b("add_selected")} ({sel.length})</button>}
    </div>
  );
}

function sourcePeriod(c) {
  const today = new Date().toISOString().slice(0, 10);
  return c.periods.find((p) => p.effective_from <= today && (!p.effective_to || p.effective_to >= today)) || c.periods[c.periods.length - 1];
}

function CopyTab({ cfg, set, b }) {
  const [list, setList] = useState([]);
  const [src, setSrc] = useState(null);
  const [sel, setSel] = useState([]);
  useEffect(() => { calcApi.list().then(setList).catch(() => setList([])); }, []);
  const pick = async (id) => {
    setSrc(null); setSel([]);
    if (!id) return;
    try {
      const c = id === "odisha" ? await calcApi.odishaTemplate() : (await calcApi.get(id)).config;
      setSrc(c);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const rows = useMemo(() => {
    if (!src) return [];
    const r = src.items.filter((i) => !i.group).map((i) => ({ id: i.key, label: `${i.emoji} ${i.name}` }));
    (src.groups || []).forEach((g) => r.push({ id: `group:${g.key}`, label: `${g.emoji} ${g.name} (${src.items.filter((i) => i.group === g.key).length})` }));
    return r;
  }, [src]);
  const doCopy = (ids) => {
    const per = sourcePeriod(src);
    const mapCk = (ck, idx) => (cfg.classes.some((x) => x.key === ck) ? ck : cfg.classes[idx]?.key);
    const cellsOf = (key) => {
      const out = {};
      src.classes.forEach((cl, idx) => { const v = per.classes?.[cl.key]?.items?.[key]; const k = mapCk(cl.key, idx); if (v && k) out[k] = { ...v }; });
      return out;
    };
    const toP = (i) => ({ ...i, key: undefined, cells: cellsOf(i.key) });
    const out = ids.map((id) => {
      if (id.startsWith("group:")) {
        const g = src.groups.find((x) => `group:${x.key}` === id);
        return { ...g, key: undefined, recipe: src.items.filter((i) => i.group === g.key).map(toP) };
      }
      return toP(src.items.find((i) => i.key === id));
    });
    set((c) => addItems(c, out));
    toast.success(`✓ ${out.length}`); setSel([]);
  };
  return (
    <div className="space-y-3" data-testid="b-copy">
      <select className="input-thali" onChange={(e) => pick(e.target.value)} defaultValue="" data-testid="b-copy-source">
        <option value="">{b("choose_calc")}</option>
        {list.map((c) => <option key={c.id} value={c.id}>{c.locked ? "🔒 " : ""}{c.name} · {c.state}{c.version ? ` · v${c.version}` : ""}</option>)}
      </select>
      {src && (
        <>
          <div className="flex gap-2">
            <button onClick={() => setSel(rows.map((r) => r.id))} className="btn-ghost text-sm flex-1" data-testid="b-copy-select-all">{b("select_all")}</button>
            <button onClick={() => doCopy(sel)} disabled={!sel.length} className="btn-3d-green text-sm flex-1 disabled:opacity-40" data-testid="b-copy-go">{b("copy_selected")} ({sel.length})</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {rows.map((r) => (
              <label key={r.id} className={`rounded-xl border-2 p-2 text-sm font-semibold flex items-center gap-2 ${sel.includes(r.id) ? "border-thali-green bg-thali-green/5" : "border-thali-turmeric/30 bg-white"}`} data-testid={`b-copy-row-${r.id}`}>
                <input type="checkbox" className="w-5 h-5 accent-thali-green" checked={sel.includes(r.id)} onChange={() => setSel((s) => (s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id]))} />
                <span className="truncate">{r.label}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ExcelTab({ cfg, set, b }) {
  const [rows, setRows] = useState(null);
  const cls = cfg.classes.map((c) => `${c.key}:${c.name.replace(/[|:]/g, " ")}`).join("|");
  const download = async () => {
    const res = await api.get("/calculators/items/template", { params: { classes: cls }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement("a");
    a.href = url; a.download = "Item_Library_Template.xlsx"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("classes", cls);
    try { setRows((await api.post("/calculators/items/parse", fd)).data.rows); } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const ok = (rows || []).filter((r) => !r.errors.length);
  const doImport = () => {
    const cellsOf = (r) => Object.fromEntries(Object.entries(r.values).map(([ck, v]) => [ck, r.cost_mode === "price"
      ? { qty: v.qty, price: v.value, rate: toRate(v.qty, r.unit, v.value) } : { qty: v.qty, rate: r.cost_mode === "free" ? 0 : v.value }]));
    const normal = ok.filter((r) => !r.group).map((r) => ({ ...r, emoji: "🍽️", cells: cellsOf(r) }));
    const groups = [...new Set(ok.filter((r) => r.group).map((r) => r.group))].map((g) => ({ name: g, emoji: "🧁",
      recipe: ok.filter((r) => r.group === g).map((r) => ({ ...r, group: undefined, emoji: "🍽️", cells: cellsOf(r) })) }));
    set((c) => addItems(c, [...normal, ...groups]));
    toast.success(`✓ ${ok.length}`); setRows(null);
  };
  return (
    <div className="space-y-3" data-testid="b-excel">
      <button onClick={download} className="btn-ghost w-full border-2 border-thali-green/40 text-thali-green" data-testid="b-excel-template"><Download className="w-4 h-4" /> {b("dl_template")}</button>
      <label className="btn-3d-saffron w-full cursor-pointer" data-testid="b-excel-upload"><Upload className="w-4 h-4" /> {b("upload_excel")}
        <input type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="b-excel-file" /></label>
      {rows && (
        <div className="space-y-1" data-testid="b-excel-preview">
          {rows.map((r) => (
            <div key={r.row} className={`rounded-lg p-2 text-sm ${r.errors.length ? "bg-red-50 text-red-700" : "bg-white border border-thali-turmeric/30"}`} data-testid={`b-excel-row-${r.row}`}>
              <b>#{r.row} {r.name || "—"}</b> · {r.unit} · {b(`cm_${r.cost_mode}`)}{r.group && ` · ${r.group}`}
              {r.errors.map((e) => <div key={e} className="text-xs">⚠️ {e}</div>)}
            </div>
          ))}
          <button onClick={doImport} disabled={!ok.length} className="btn-3d-green w-full disabled:opacity-40" data-testid="b-excel-import">{b("import")} ({ok.length})</button>
        </div>
      )}
    </div>
  );
}

const TABS = [["manual", "✍️", ManualForm], ["presets", "📚", PresetsTab], ["copy", "📋", CopyTab], ["excel", "📥", ExcelTab]];

export default function ItemAddTabs(props) {
  const [tab, setTab] = useState("manual");
  const Comp = TABS.find((x) => x[0] === tab)[2];
  return (
    <div className="rounded-2xl bg-thali-cream/60 border border-thali-turmeric/30 p-3 space-y-3">
      <div className="grid grid-cols-4 gap-1" data-testid="b-add-tabs">
        {TABS.map(([k, ic]) => (
          <button key={k} onClick={() => setTab(k)} className={`min-h-[48px] rounded-xl text-[11px] sm:text-sm font-bold leading-tight px-1 transition-colors ${tab === k ? "bg-thali-green text-white" : "bg-white text-thali-ink/70"}`} data-testid={`b-tab-${k}`}>
            {ic} {props.b(`tab_${k}`)}
          </button>
        ))}
      </div>
      <Comp {...props} />
    </div>
  );
}
